import { DynamicFurnitureShape } from "../DynamicFurnitureShape/DynamicFurnitureShape";
import Konva from "konva";
import React from "react";
import { Rect } from "react-konva";
import {
  computeNeighborDistances,
  getGroupSnapPosition,
  getShapeBounds,
  getSnapPosition
} from "../../../../utils/snap";
import {
  hideShapeToolBoxes,
  showShapeToolBoxes
} from "../../../../utils/toolbox";
import { GroupShape as GroupShapeComponent } from "../../../GroupShape";
import { SnapLines } from "../../../SnapLines";
import { useMapCanvas } from "../../contexts/MapCanvasContext";
import {
  MapEditorState,
  useMapEditorActions
} from "../../contexts/MapEditorContext";
import { SELECTION_STROKE_COLOR } from "../../defines";
import { ShapeCommonProps } from "../../types";
import { CircleShape } from "../CircleShape/CircleShape";
import { FurnitureShape } from "../FurnitureShape/FurnitureShape";
import { IconShape } from "../IconShape/IconShape";
import { RectShape } from "../RectShape/RectShape";
import { useShape } from "../ShapeContext/useShape";
import { ShapeTransformer } from "../ShapeTransformer";
import { SpotShapeComponent } from "../SpotShape/SpotShape";
import { SvgShape } from "../SvgShape/SvgShape";
import { TextShape } from "../TextShape/TextShape";
import { Shape, CircleShape as ICircleShape, WallShape as IWallShape, WallPathShape as IWallPathShape, SpotShape as ISpotShape, ZoneShape as IZoneShape } from "../../../../types";
import { computeWallPathBounds } from "../../../../utils/shapeFactory";
import { ImageShape } from "../ImageShape/ImageShape";
import { ZoneShape } from "../ZoneShape/ZoneShape";
import { WallShape } from "../WallShape/WallShape";
import { WallPathShapeComponent } from "../WallPathShape/WallPathShape";

export interface DraggableShapeProps {
  setContextMenuShape?: MapEditorState["setContextMenuShape"];
  setHighlightedShape?: MapEditorState["setHighlightedShape"];
  highlightedShape?: MapEditorState["highlightedShape"];
  onStartDragDuplicate?: () => void;
  isDragging?: boolean;
}

export const DraggableShape: React.FC<DraggableShapeProps> = React.memo(({
  setContextMenuShape,
  setHighlightedShape,
  highlightedShape,
  onStartDragDuplicate,
  isDragging = false
}) => {
  const { isViewMode, updateEditingZoneSize, shapesRef } = useMapCanvas();
  // Read the selection from the ref: subscribing to the selection context
  // here made every shape on the canvas re-render on each click.
  const { setShapes, selectedIdsRef } = useMapEditorActions();
  const {
    isEditing,
    setIsEditing,
    setSnapLines,
    snapLines,
    setIsHovered,
    isHovered,
    shape,
    isSelected,
    onSelect,
    onUpdate,
    isGroupChild,
    editorConfigRef,
    otherShapes,
    disabled,
    isMultiSelected
  } = useShape();
  const shapeRef = React.useRef<any>(null);
  const transformerRef =
    React.useRef<React.ComponentRef<typeof ShapeTransformer>>(null);
  // Guard: ensure we commit the multi-drag translation only once per drag
  // session, even if dragend fires multiple times due to event bubbling or
  // React concurrent re-entry.
  const multiDragCommittedRef = React.useRef(false);
  // Stored positions of every selected shape captured at drag start. Used
  // as the authoritative baseline so the committed position is
  // startPos + delta — never compounded against a stored x that might have
  // been mutated elsewhere mid-drag.
  const multiDragStartPositionsRef = React.useRef<
    Map<string, { x: number; y: number }>
  >(new Map());

  React.useEffect(() => {
    if (
      isSelected &&
      !isMultiSelected &&
      transformerRef.current &&
      shapeRef.current
    ) {
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected, isMultiSelected, shape]);

  // Measurement overlay on selection + Alt hold. Lets the user nudge
  // the shape with arrow keys (handled in MapEditorProvider) and see
  // edge-to-edge distances to neighbors update live. We use Alt rather
  // than Shift because Shift+arrow is already bound to "move by 5×",
  // and that conflict made precision nudging useless. Distances are
  // read from the live Konva node position so arrow-key nudges (which
  // move the node directly and commit to React state on a 300ms
  // debounce) reflect immediately.
  const liveShapeRef = React.useRef(shape);
  liveShapeRef.current = shape;
  const liveOtherShapesRef = React.useRef(otherShapes);
  liveOtherShapesRef.current = otherShapes;
  React.useEffect(() => {
    if (!isSelected || isMultiSelected) return;

    const computeAndSet = () => {
      const s = liveShapeRef.current;
      if (!s) return;
      const liveShapes = shapesRef?.current || liveOtherShapesRef.current || [];
      const node = shapeRef.current;
      const storedBounds = getShapeBounds(s);
      const liveX = node ? node.x() : s.x;
      const liveY = node ? node.y() : s.y;
      const dx = liveX - s.x;
      const dy = liveY - s.y;
      const liveBounds = {
        left: storedBounds.left + dx,
        right: storedBounds.right + dx,
        top: storedBounds.top + dy,
        bottom: storedBounds.bottom + dy
      };
      const distances = computeNeighborDistances(
        liveBounds,
        liveShapes,
        new Set([s.id])
      );
      setSnapLines((prev) => ({ ...prev, distances }));
    };
    const clear = () => {
      setSnapLines((prev) =>
        prev.distances ? { ...prev, distances: undefined } : prev
      );
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey) {
        computeAndSet();
      } else {
        clear();
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
      clear();
    };
  }, [isSelected, isMultiSelected, setSnapLines, shapesRef]);

  // The shape is hidden, don't render it
  if (shape.hidden) {
    return null;
  }

  const snapToGridValue = (value: number): number => {
    const cfg = editorConfigRef?.current;
    if (!cfg?.snapToGrid) return value;
    return Math.round(value / cfg.gridSize) * cfg.gridSize;
  };

  const handleDragStart = (event: Konva.KonvaEventObject<DragEvent>) => {
    hideShapeToolBoxes();
    if (event.evt.altKey) {
      onStartDragDuplicate?.();
    }

    if (!isSelected && !disabled && !shape.locked) {
      onSelect(event);
    }

    multiDragCommittedRef.current = false;
    multiDragStartPositionsRef.current = new Map();
    if (isMultiSelected) {
      const selectedSet = new Set(selectedIdsRef.current);
      for (const s of [shape, ...(otherShapes || [])]) {
        if (selectedSet.has(s.id) && !s.locked) {
          multiDragStartPositionsRef.current.set(s.id, { x: s.x, y: s.y });
        }
      }
    }
  };

  const handleDragMove = (event: Konva.KonvaEventObject<DragEvent>) => {
    if (isGroupChild || disabled || shape.locked) return;

    const node = event.target;
    const cfg = editorConfigRef?.current;
    const stage = node.getStage();
    const stageScale = stage?.scaleX() ?? 1;

    // Multi-select: move every selected shape together and snap against
    // the rest of the map using the combined bounding box.
    if (isMultiSelected && stage) {
      const startPositions = multiDragStartPositionsRef.current;
      const grabbedStart = startPositions.get(shape.id);
      if (!grabbedStart) return;

      // Read shapes fresh from the ref so snap sees the latest state
      // (e.g., composite templates applied after this component rendered).
      const liveShapes = shapesRef?.current || [];
      const selectedSet = new Set(selectedIdsRef.current);
      const groupShapes: Shape[] = liveShapes.filter(
        (s) => selectedSet.has(s.id) && !s.locked && startPositions.has(s.id)
      );

      // Delta from the grabbed shape's captured start position (not from its
      // current stored state — which could have been mutated mid-drag).
      let dx = node.x() - grabbedStart.x;
      let dy = node.y() - grabbedStart.y;

      if (cfg?.showSnapLines) {
        const groupBounds = groupShapes.reduce(
          (acc, s) => {
            const start = startPositions.get(s.id)!;
            const b = getShapeBounds(s);
            // Translate bounds from current stored to captured start, then
            // shift by the in-progress drag delta.
            const offsetX = start.x - s.x + dx;
            const offsetY = start.y - s.y + dy;
            return {
              left: Math.min(acc.left, b.left + offsetX),
              right: Math.max(acc.right, b.right + offsetX),
              top: Math.min(acc.top, b.top + offsetY),
              bottom: Math.max(acc.bottom, b.bottom + offsetY)
            };
          },
          {
            left: Number.POSITIVE_INFINITY,
            right: Number.NEGATIVE_INFINITY,
            top: Number.POSITIVE_INFINITY,
            bottom: Number.NEGATIVE_INFINITY
          }
        );

        const snapTargets = liveShapes.filter(
          (s) => !selectedSet.has(s.id)
        );
        const groupSnap = getGroupSnapPosition(
          groupBounds,
          snapTargets,
          stageScale
        );

        if (groupSnap.delta.x !== undefined) dx += groupSnap.delta.x;
        if (groupSnap.delta.y !== undefined) dy += groupSnap.delta.y;

        setSnapLines({
          x: groupSnap.lines.x,
          y: groupSnap.lines.y,
          targetX: groupSnap.targetDot.x,
          targetY: groupSnap.targetDot.y
        });
      } else {
        setSnapLines({});
      }

      // Grid snap runs last so the final delta is always grid-aligned even
      // when a snap line pulled the group to a fractional position. Skip
      // when the user is in precision mode (Shift held) so they can park
      // the selection at a sub-pixel / non-grid position.
      if (cfg?.snapToGrid && !event.evt.shiftKey) {
        dx = snapToGridValue(grabbedStart.x + dx) - grabbedStart.x;
        dy = snapToGridValue(grabbedStart.y + dy) - grabbedStart.y;
      }

      for (const s of groupShapes) {
        const start = startPositions.get(s.id);
        if (!start) continue;
        const targetNode = stage.findOne(`#shape-${s.id}`);
        if (targetNode) {
          targetNode.position({ x: start.x + dx, y: start.y + dy });
        }
      }
      return;
    }

    let newX = node.x();
    let newY = node.y();

    if (cfg?.showSnapLines) {
      // Read shapes fresh from the ref so snap sees the latest state
      // (e.g., composite templates applied after this component rendered).
      const liveShapes = shapesRef?.current || otherShapes || [];
      const snapResult = getSnapPosition(
        shape,
        liveShapes,
        { x: newX, y: newY },
        shape.id,
        stageScale
      );

      // Precision mode: Shift held during drag turns OFF snap attraction
      // and turns ON the distance overlay. We still compute the snap
      // result so the alignment lines stay visible (the user can see
      // where they would snap to), but the position is left alone so
      // they can park the shape anywhere with sub-pixel precision.
      // We use Shift instead of Alt because Alt is already bound to
      // duplicate-on-drag.
      const isPrecisionMode = event.evt.shiftKey;

      if (!isPrecisionMode) {
        // Apply snap attraction — override position (grid snap runs after)
        if (snapResult.position.x !== undefined) {
          newX = snapResult.position.x;
        }
        if (snapResult.position.y !== undefined) {
          newY = snapResult.position.y;
        }
      }

      // Compute distances to neighbors in precision mode. Use the active
      // shape's VISUAL bounds at the current position — not just
      // (newX..newX+shape.width). For spots, shape.width is the wrapping
      // group's nominal size (e.g. 100), but the visible desk rect is
      // narrower (e.g. 90), so naively using shape.width would shoot the
      // measurement line ~10px past the visible right edge.
      let distances: ReturnType<typeof computeNeighborDistances> | undefined;
      if (isPrecisionMode) {
        const storedBounds = getShapeBounds(shape);
        const dx = newX - shape.x;
        const dy = newY - shape.y;
        const activeAtCurrent = {
          left: storedBounds.left + dx,
          right: storedBounds.right + dx,
          top: storedBounds.top + dy,
          bottom: storedBounds.bottom + dy
        };
        distances = computeNeighborDistances(
          activeAtCurrent,
          liveShapes,
          new Set([shape.id])
        );
      }

      // Show snap lines regardless of precision mode — the lines now mean
      // "this is where alignment would land", not "snap is applied".
      setSnapLines({
        x: snapResult.lines.x,
        y: snapResult.lines.y,
        targetX: snapResult.targetDot.x,
        targetY: snapResult.targetDot.y,
        centeredIn: snapResult.centeredIn,
        activeBounds: snapResult.activeBounds,
        distances
      });
    } else {
      setSnapLines({});
    }

    // Grid snap runs last so the final position is always grid-aligned even
    // when a snap line pulled the shape to a fractional position. Skip in
    // precision mode (Shift held) — the whole point of that mode is to let
    // the user park the shape at a sub-pixel / non-grid position.
    if (cfg?.snapToGrid && !event.evt.shiftKey) {
      newX = snapToGridValue(newX);
      newY = snapToGridValue(newY);
    }

    node.position({ x: newX, y: newY });
  };

  const handleDragEnd = (event: Konva.KonvaEventObject<DragEvent>) => {
    showShapeToolBoxes();
    if (!isGroupChild && !disabled && !shape.locked) {
      const node = event.target;
      const newX = Math.round(node.x());
      const newY = Math.round(node.y());

      // Multi-select: only the grabbed shape fires dragend, so commit the
      // same translation to every selected shape in one atomic update.
      // Commit runs once per drag session.
      //
      // The delta is measured from a *non-grabbed* selected node because
      // Konva's drag engine may reset the grabbed node to the raw pointer
      // position on mouseup (discarding our snap override). Non-grabbed
      // nodes only move via our dragmove overrides, so they hold the exact
      // snapped delta we last applied. Positions are committed by adding
      // the delta to each shape's *captured start* position — avoids any
      // compounding with a stored x that might change mid-drag.
      if (isMultiSelected) {
        if (multiDragCommittedRef.current) {
          setSnapLines({});
          return;
        }
        multiDragCommittedRef.current = true;

        const stage = node.getStage();
        const selectedSet = new Set(selectedIdsRef.current);
        const startPositions = multiDragStartPositionsRef.current;
        const grabbedStart = startPositions.get(shape.id);

        const reference = (otherShapes || []).find(
          (s) =>
            s.id !== shape.id &&
            selectedSet.has(s.id) &&
            !s.locked &&
            startPositions.has(s.id)
        );

        let dx: number;
        let dy: number;
        if (reference && stage) {
          const refNode = stage.findOne(`#shape-${reference.id}`);
          const refStart = startPositions.get(reference.id);
          if (refNode && refStart) {
            dx = Math.round(refNode.x()) - refStart.x;
            dy = Math.round(refNode.y()) - refStart.y;
          } else if (grabbedStart) {
            dx = newX - grabbedStart.x;
            dy = newY - grabbedStart.y;
          } else {
            dx = newX - shape.x;
            dy = newY - shape.y;
          }
        } else if (grabbedStart) {
          dx = newX - grabbedStart.x;
          dy = newY - grabbedStart.y;
        } else {
          dx = newX - shape.x;
          dy = newY - shape.y;
        }

        if (dx === 0 && dy === 0) {
          setSnapLines({});
          return;
        }

        setShapes((prev) =>
          prev.map((s): Shape => {
            if (!selectedSet.has(s.id) || s.locked) return s;
            const start = startPositions.get(s.id);
            if (!start) return s;
            const nextX = Math.round(start.x + dx);
            const nextY = Math.round(start.y + dy);
            const updated: Shape = { ...s, x: nextX, y: nextY } as Shape;
            if (s.type === "wall") {
              const wall = s as IWallShape;
              (updated as IWallShape).x2 = Math.round(wall.x2 - wall.x + nextX);
              (updated as IWallShape).y2 = Math.round(wall.y2 - wall.y + nextY);
            }
            if (s.type === "wall-path") {
              const path = s as IWallPathShape;
              (updated as IWallPathShape).vertices = path.vertices.map((v) => ({
                ...v,
                x: Math.round(v.x - path.x + nextX),
                y: Math.round(v.y - path.y + nextY)
              }));
            }
            return updated;
          })
        );
        setSnapLines({});
        return;
      }

      const updateData: Partial<Shape> = { x: newX, y: newY };

      if (shape.type === "wall") {
        const wallShape = shape as IWallShape;
        const dx = wallShape.x2 - wallShape.x;
        const dy = wallShape.y2 - wallShape.y;
        (updateData as Partial<IWallShape>).x2 = Math.round(newX + dx);
        (updateData as Partial<IWallShape>).y2 = Math.round(newY + dy);
      }

      if (shape.type === "wall-path") {
        const pathShape = shape as IWallPathShape;
        const dx = newX - pathShape.x;
        const dy = newY - pathShape.y;
        const newVertices = pathShape.vertices.map((v) => ({
          ...v,
          x: Math.round(v.x + dx),
          y: Math.round(v.y + dy)
        }));
        const bounds = computeWallPathBounds(newVertices);
        (updateData as Partial<IWallPathShape>).vertices = newVertices;
        updateData.width = bounds.width;
        updateData.height = bounds.height;
      }

      onUpdate(updateData);
    }
    setSnapLines({});
  };

  const handleTransformEnd = (e: Konva.KonvaEventObject<Event>) => {
    e.cancelBubble = true;
    if (!isGroupChild && !disabled) {
      const node = shapeRef.current;

      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      const isRotationOnly =
        Math.abs(Math.abs(scaleX) - 1) < 0.001 && Math.abs(Math.abs(scaleY) - 1) < 0.001;

      // Resize bakes the scale magnitude into width/height, so reset the
      // node's scale to the original flip sign (the resize handles cannot
      // create a flip on their own — the user uses dedicated controls).
      // Skip this reset for pure rotation: Konva's transformer can rebase
      // a flipped node's rotation by toggling the scale sign during a
      // rotation drag, and overriding scale here would un-do that rebasing
      // and produce a visual jump on release.
      if (!isRotationOnly) {
        const flipSignX = (shape.scaleX || 1) < 0 ? -1 : 1;
        const flipSignY = (shape.scaleY || 1) < 0 ? -1 : 1;
        node.scaleX(flipSignX);
        node.scaleY(flipSignY);
      }

      // During pure rotation we keep x, y AND rotation as floats. Konva's
      // transformer computes each shape's new x/y from the un-rounded
      // rotation angle (e.g. 29.99999° rather than 30° even when snapping).
      // Rounding rotation while keeping x/y exact leaves them describing
      // slightly different rotations, which renders as small per-shape
      // drift — most visible at non-snap angles and after multi-select
      // rotations. Resize keeps the existing rounding so dimensions stay
      // clean.
      const updateData: Partial<Shape> = {
        x: isRotationOnly ? node.x() : Math.round(node.x()),
        y: isRotationOnly ? node.y() : Math.round(node.y()),
        rotation: isRotationOnly ? node.rotation() : Math.round(node.rotation()),
        // Capture the node's current scale signs. After a pure rotation of
        // a flipped shape Konva may have toggled the sign and added 180°
        // to rotation to express the same visual — keeping these in sync
        // with the React state prevents a snap-back on release.
        scaleX: Math.sign(node.scaleX()) || 1,
        scaleY: Math.sign(node.scaleY()) || 1
      };

      // Only update width/height when actually resizing — reading them from
      // the Konva node during pure rotation can produce different values than
      // the stored shape dimensions, causing a visual jump on release.
      if (!isRotationOnly) {
        updateData.width = Math.round(Math.max(5, node.width() * Math.abs(scaleX)));
        updateData.height = Math.round(Math.max(5, node.height() * Math.abs(scaleY)));
      }

      if (shape.type === "circle") {
        (updateData as ICircleShape).radius = Math.round(
          Math.max(updateData.width || 0, updateData.height || 0) / 2
        );
      }

      if (shape.type === "zone" && !isRotationOnly) {
        const zoneShape = shape as IZoneShape;
        // Polygon zones store their outline relative to the origin — a
        // resize must scale the vertices (and each edge's curvature,
        // which is a perpendicular distance) or the outline detaches
        // from the transformed bounds.
        if (zoneShape.vertices?.length) {
          const vertexScaleX = Math.abs(scaleX);
          const vertexScaleY = Math.abs(scaleY);
          (updateData as Partial<IZoneShape>).vertices =
            zoneShape.vertices.map((vertex) => ({
              x: Math.round(vertex.x * vertexScaleX),
              y: Math.round(vertex.y * vertexScaleY)
            }));
          if (zoneShape.edges?.length) {
            const curvatureScale = (vertexScaleX + vertexScaleY) / 2;
            (updateData as Partial<IZoneShape>).edges = zoneShape.edges.map(
              (edge) =>
                edge.curvature
                  ? {
                      ...edge,
                      curvature: Math.round(edge.curvature * curvatureScale)
                    }
                  : edge
            );
          }
        }
      }

      if (shape.type === "wall") {
        const wallShape = shape as IWallShape;
        // A wall's geometry is already encoded in (x, y) → (x2, y2);
        // a stored rotation is redundant and gets ignored by every
        // downstream consumer (floor-fill polygon graph, hover/select
        // outline, save/reload, snap matcher), so the wall RENDERS
        // rotated in the editor but persists as if it weren't, and the
        // background fill + outline land in the wrong place. Bake the
        // current rotation into the endpoints here so x2/y2 reflect the
        // visual position, then reset rotation/scale on both the data
        // and the Konva node so they don't double-apply on next render.
        //
        // Use SIGNED scaleX/scaleY (not Math.abs). Konva.Transformer
        // sometimes expresses certain rotations — notably 180° — as
        // sign-flips on scale instead of a rotation angle (same visual,
        // different decomposition). If we strip the sign with Math.abs
        // we lose that part of the transform and the wall ends up at
        // the wrong endpoints despite the rotation having "happened".
        const dxLocal = (wallShape.x2 - wallShape.x) * scaleX;
        const dyLocal = (wallShape.y2 - wallShape.y) * scaleY;
        const rotRad = (node.rotation() * Math.PI) / 180;
        const cosR = Math.cos(rotRad);
        const sinR = Math.sin(rotRad);
        const rotatedDx = dxLocal * cosR - dyLocal * sinR;
        const rotatedDy = dxLocal * sinR + dyLocal * cosR;
        (updateData as Partial<IWallShape>).x2 = Math.round(
          (updateData.x || 0) + rotatedDx
        );
        (updateData as Partial<IWallShape>).y2 = Math.round(
          (updateData.y || 0) + rotatedDy
        );
        updateData.rotation = 0;
        updateData.scaleX = 1;
        updateData.scaleY = 1;
        node.rotation(0);
        node.scaleX(1);
        node.scaleY(1);
      }

      if (shape.type === "wall-path") {
        const pathShape = shape as IWallPathShape;
        const originX = pathShape.x;
        const originY = pathShape.y;
        // Like plain walls, wall-path vertices are stored in world
        // coords and downstream consumers ignore a stored rotation —
        // bake it into the vertices and reset rotation. Use SIGNED
        // scaleX/scaleY so any sign-flip Konva.Transformer absorbed
        // from a 180° rotation gets baked too (see wall branch above).
        //
        // The Konva node origin (node.x() / node.y()) is wherever the
        // transformer left the Group after rotation, but a wall-path's
        // stored `x`/`y` is required to be the *bbox top-left* of its
        // vertices (per createWallPath / computeWallPathBounds). After
        // rotation those two points don't coincide, so we compute the
        // new vertices using the node origin as a reference, then
        // override updateData.x / y with the bbox top-left so the
        // shape's invariants hold. The visual is unchanged because
        // vertices are world coords and the renderer draws them
        // relative to pathShape.x / y.
        const rotRad = (node.rotation() * Math.PI) / 180;
        const cosR = Math.cos(rotRad);
        const sinR = Math.sin(rotRad);
        const anchorX = updateData.x || 0;
        const anchorY = updateData.y || 0;
        const newVertices = pathShape.vertices.map((v) => {
          const lx = (v.x - originX) * scaleX;
          const ly = (v.y - originY) * scaleY;
          const rx = lx * cosR - ly * sinR;
          const ry = lx * sinR + ly * cosR;
          return {
            ...v,
            x: Math.round(anchorX + rx),
            y: Math.round(anchorY + ry)
          };
        });
        const bounds = computeWallPathBounds(newVertices);
        (updateData as Partial<IWallPathShape>).vertices = newVertices;
        updateData.x = bounds.x;
        updateData.y = bounds.y;
        updateData.width = bounds.width;
        updateData.height = bounds.height;
        updateData.rotation = 0;
        updateData.scaleX = 1;
        updateData.scaleY = 1;
        // Sync the Konva node so the transformer attached to it reads
        // a position consistent with the about-to-commit shape state.
        node.x(bounds.x);
        node.y(bounds.y);
        node.rotation(0);
        node.scaleX(1);
        node.scaleY(1);
      }

      const isFloorPlanShape =
        (shape.layerType === "floor" || shape.layerType === "background") &&
        (shape.type === "image" || shape.type === "svg");

      if (isFloorPlanShape && updateData.width && updateData.height) {
        const offsetX = -(updateData.x || 0);
        const offsetY = -(updateData.y || 0);
        updateData.x = 0;
        updateData.y = 0;
        updateEditingZoneSize(updateData.width, updateData.height);

        setShapes((prev) =>
          prev.map((s): Shape => {
            if (s.id === shape.id) {
              return { ...s, ...updateData } as Shape;
            }
            if (offsetX !== 0 || offsetY !== 0) {
              const updated: Shape = { ...s, x: (s.x || 0) + offsetX, y: (s.y || 0) + offsetY } as Shape;
              if (s.type === "wall") {
                const wall = updated as IWallShape;
                wall.x2 = (wall.x2 || 0) + offsetX;
                wall.y2 = (wall.y2 || 0) + offsetY;
              }
              if (s.type === "wall-path") {
                const path = updated as IWallPathShape;
                path.vertices = path.vertices.map((v) => ({
                  ...v,
                  x: v.x + offsetX,
                  y: v.y + offsetY
                }));
              }
              return updated;
            }
            return s;
          })
        );
      } else {
        onUpdate(updateData);
      }
    }
  };

  const handleContextMenu = () => {
    if (shape.locked) {
      return;
    }
    setContextMenuShape?.(shape);
  };

  const handleOnClick = (event: Konva.KonvaEventObject<MouseEvent>) => {
    if (shape.locked) {
      return;
    }

    onSelect(event);
  };

  const handleDblClick = (event: Konva.KonvaEventObject<MouseEvent>) => {
    if (shape.locked) return;
    if (shape.type === "wall-path" && isSelected && !isEditing) {
      event.cancelBubble = true;
      setIsEditing(true);
    }
  };

  const handleMouseEnter = () => {
    if (isViewMode || shape.locked) return;
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    if (isViewMode || shape.locked) return;
    setIsHovered(false);
  };

  const commonProps: ShapeCommonProps = {
    id: `shape-${shape.id}`,
    x: shape.x,
    y: shape.y,
    draggable:
      !isDragging && !isViewMode && !isGroupChild && !disabled && !shape.locked
      // Walls are only draggable when selected, so rubber band selection can pass through them
      && ((shape.type !== "wall" && shape.type !== "wall-path") || isSelected),
    rotation: shape.rotation || 0,
    scaleX: shape.scaleX && shape.scaleX !== 1 ? shape.scaleX : undefined,
    scaleY: shape.scaleY && shape.scaleY !== 1 ? shape.scaleY : undefined,
    onClick: handleOnClick,
    onDblClick: handleDblClick,
    onTap: onSelect,
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    onTransformEnd: handleTransformEnd,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onContextMenu: handleContextMenu,
    highlightShapeData:
      highlightedShape?.shape?.id === shape.id ? highlightedShape : undefined,
    ref: shapeRef
  };

  if (shape.type === "group") {
    return <GroupShapeComponent {...commonProps} />;
  }

  return (
    <>
      {shape.type === "spot" ? (
        <SpotShapeComponent
          highlightShape={setHighlightedShape}
          {...commonProps}
        />
      ) : shape.type === "circle" ? (
        <CircleShape {...commonProps} />
      ) : shape.type === "text" ? (
        <TextShape {...commonProps} highlightShape={setHighlightedShape} />
      ) : shape.type === "image" ? (
        <ImageShape {...commonProps} />
      ) : shape.type === "svg" ? (
        <SvgShape {...commonProps} />
      ) : shape.type === "dynamic-furniture" ? (
        <DynamicFurnitureShape {...commonProps} />
      ) : shape.type === "furniture" ? (
        <FurnitureShape {...commonProps} />
      ) : shape.type === "icon" ? (
        <IconShape {...commonProps} />
      ) : shape.type === "zone" ? (
        <ZoneShape {...commonProps} />
      ) : shape.type === "wall" ? (
        <WallShape {...commonProps} />
      ) : shape.type === "wall-path" ? (
        <WallPathShapeComponent {...commonProps} />
      ) : (
        <RectShape {...commonProps} />
      )}

      {isHovered && !isSelected && (() => {
        // Simple spots (no companions) already get an inline hover stroke
        // from SpotItem. Composite spots need an outer bbox outline that
        // covers the anchor + every companion.
        const isSimpleSpot =
          shape.type === "spot" &&
          !(shape as ISpotShape).companions?.length;
        if (isSimpleSpot) return null;

        // Walls live on their endpoints (x, y) → (x2, y2) with `width` /
        // `height` left at 0, so the generic centroid-anchored rect at
        // the bottom of this block draws a zero-sized box at shape.x /
        // shape.y — which is the visible "outline shown in the wrong
        // position" the user reported. Compute the axis-aligned hover
        // bbox from the actual endpoints plus half the wall thickness
        // for a bit of padding. Rotation is already baked into the
        // endpoints by handleTransformEnd, so no rotation handling
        // needed here.
        if (shape.type === "wall") {
          const wall = shape as IWallShape;
          const halfT = (wall.thickness || 10) / 2;
          const left = Math.min(wall.x, wall.x2) - halfT;
          const right = Math.max(wall.x, wall.x2) + halfT;
          const top = Math.min(wall.y, wall.y2) - halfT;
          const bottom = Math.max(wall.y, wall.y2) + halfT;
          return (
            <Rect
              stroke={SELECTION_STROKE_COLOR}
              strokeWidth={1}
              x={left}
              y={top}
              width={right - left}
              height={bottom - top}
              listening={false}
            />
          );
        }

        // wall-path and spot already use world-space bounds, no rotation
        if (shape.type === "wall-path") {
          const wpThickness = (shape as IWallPathShape).thickness || 10;
          const half = wpThickness / 2;
          return (
            <Rect
              stroke={SELECTION_STROKE_COLOR}
              strokeWidth={1}
              x={shape.x - half}
              y={shape.y - half}
              width={shape.width + wpThickness}
              height={shape.height + wpThickness}
              listening={false}
            />
          );
        }
        if (shape.type === "spot") {
          const bounds = getShapeBounds(shape);
          return (
            <Rect
              stroke={SELECTION_STROKE_COLOR}
              strokeWidth={1}
              x={bounds.left}
              y={bounds.top}
              width={bounds.right - bounds.left}
              height={bounds.bottom - bounds.top}
              listening={false}
            />
          );
        }

        // For all other shape types, render a Rect centered at the shape's
        // visual center with the same rotation but NO scale. The flip handler
        // adjusts shape.x/y so the visual bbox stays W×H regardless of
        // scaleX/scaleY sign — a center-anchored rect of width×height is
        // symmetric, so it lines up whether the shape is flipped or not.
        // Compute the visual center by transforming the local point (W/2, H/2)
        // into world coords using the same transform Konva applies:
        //   world = translate(x, y) · rotate · scale · localPoint
        const sX = shape.scaleX ?? 1;
        const sY = shape.scaleY ?? 1;
        const rad = ((shape.rotation ?? 0) * Math.PI) / 180;
        const cosT = Math.cos(rad);
        const sinT = Math.sin(rad);
        // For circles, shape.x/y is already the center; for other shapes
        // it's the local origin and we offset by half width/height in
        // local space before applying rotation/scale.
        const localCenterX = shape.type === "circle" ? 0 : shape.width / 2;
        const localCenterY = shape.type === "circle" ? 0 : shape.height / 2;
        const scaledX = sX * localCenterX;
        const scaledY = sY * localCenterY;
        const centerX = shape.x + scaledX * cosT - scaledY * sinT;
        const centerY = shape.y + scaledX * sinT + scaledY * cosT;

        return (
          <Rect
            stroke={SELECTION_STROKE_COLOR}
            strokeWidth={1}
            x={centerX}
            y={centerY}
            width={shape.width}
            height={shape.height}
            offsetX={shape.width / 2}
            offsetY={shape.height / 2}
            rotation={shape.rotation}
            listening={false}
          />
        );
      })()}

      {isSelected &&
        !isEditing &&
        !isGroupChild &&
        !disabled &&
        !isMultiSelected && (() => {
          // Floor-plan images (the SVG/raster on floor or background layers)
          // are uploaded with locked=true so users don't accidentally move
          // or resize them while drafting walls on top. Rotation is the
          // exception — useful when the source plan is captured at an
          // angle. We only relax the rotate gate for these specific shapes
          // so other locked shapes still behave the same.
          const isFloorPlanImage =
            (shape.layerType === "floor" ||
              shape.layerType === "background") &&
            (shape.type === "image" || shape.type === "svg");
          return (
            <ShapeTransformer
              ref={transformerRef}
              resizeEnabled={shape.type !== "spot"}
              rotateEnabled={!shape.locked || isFloorPlanImage}
            />
          );
        })()}
      {!isGroupChild && editorConfigRef?.current?.showSnapLines && <SnapLines snapLines={snapLines} />}
    </>
  );
});

DraggableShape.displayName = "DraggableShape";
